/**
 * Grid and spacing constants.

 * Exposed on the MUI theme via:
 *   interface Theme { layout: typeof layoutTokens }
 *
 * Access anywhere with:
 *   const { layout } = useTheme();
 *   layout.grid.gutter, layout.grid.contentSpan.default, etc.
 * 
 *  FIGMA reference: 
 *  https://www.figma.com/design/CqO24eewdxuBg3MkLbj2QX/BHB-components?node-id=6112-51415&t=cqV7qec26xmWuSV7-0
 */

export const layoutTokens = {
  grid: {
    /** Outer max-width of the layout shell (px) */
    maxWidth: 1500,

    /** Number of columns */
    columns: 12,

    /** Gap between columns (px) */
    gutter: 16,

    /** Outer horizontal padding per breakpoint (px) */
    margin: {
      layoutXl: 24,
      layoutLg: 24,
      layoutMd: 24,
      layoutSm: 16,
      default: 16
    },

    /** Minimum vertical spacing between stacked elements (px) */
    verticalUnit: 16,

    /** Gap between app header bar and content area (px) */
    headerContentGap: 24,

    /**
     * Column-span presets (out of 12 columns).
     * Use these in layout components instead of magic numbers.
     *
     * default → most screens (main content, centred)
     * narrow  → forms, steppers
     * full    → tables, calendars
     */
    contentSpan: {
      default: 6,
      narrow: 4,
      full: 12
    }
  },

  /**
   * Spacing scale — multiples of 8px base unit.
   * Use theme.layout.spacing.md instead of hard-coded values.
   */
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48
  },

  /**
   * Border radius scale.
   * Figma: badgeCornerRadius = 4
   */
  borderRadius: {
    badge: 4,
    card: 8,
    modal: 20,
    pill: 999
  }
} as const;

export type LayoutTokens = typeof layoutTokens;
